import { FC, useMemo } from "react";
import { Alert, Skeleton } from "react-daisyui";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ArrowRightStartOnRectangleIcon,
  Cog6ToothIcon,
  EllipsisHorizontalIcon,
  HomeIcon,
  MapIcon,
  UserCircleIcon,
} from "@heroicons/react/24/outline";

import { cn } from "@/utils/classNames.ts";
import { PropsWithClassName } from "@/types/PropsWithClassName";
import { useAuthContext } from "@/context/AuthContext.tsx";
import TrialBlock from "@/components/functional/TrialBlock/TrialBlock.tsx";
import NewTripButton from "@/components/functional/NewTripButton/NewTripButton.tsx";
import TripBlock from "@/components/functional/TripBlock/TripBlock.tsx";
import useItineraries from "@/api/itineraries/useItineraries.ts";
import useTrips from "@/api/trips/useTrips.ts";
import tripListItineraries, { pairWithTrips } from "@/utils/trip/tripListItineraries.ts";
import { useTripLayoutContext } from "@/layouts/TripLayout/TripLayoutProvider.tsx";
import Routes from "@/router/Routes.ts";

const Navbar: FC<PropsWithClassName> = ({ className }) => {
  const { t } = useTranslation();
  const { logout, user, subscription, subscriptionDaysLeft } = useAuthContext();
  const { setIsMenuOpen } = useTripLayoutContext();
  const {
    data: itineraries,
    isLoading: isLoadingItineraries,
    error: errorLoadingItineraries,
  } = useItineraries(!!user, {
    user_id: user?.id,
  });

  const { data: trips } = useTrips(!!user?.id, {
    user_id: user?.id,
  });

  const { ready: visibleItineraries, failed: failedItineraries } = useMemo(
    () => tripListItineraries(itineraries ?? [], Date.now()),
    [itineraries],
  );

  const failedTrips = useMemo(
    () => pairWithTrips(failedItineraries, trips),
    [failedItineraries, trips],
  );

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <nav className={cn("flex min-h-0 flex-col text-[#6F5D26]", className)}>
      <div className="shrink-0 space-y-1">
        <Link
          to={Routes.DASHBOARD}
          onClick={closeMenu}
          className="flex items-center gap-4 border-b border-primary/35 px-4 py-4 font-sans text-lg font-medium transition-colors hover:bg-white/30"
        >
          <HomeIcon className="h-7 w-7 fill-current" aria-hidden="true" />
          <span>{t("tripNavbar.home", { defaultValue: "Home" })}</span>
        </Link>

        <div className="flex items-center gap-4 px-4 pb-2 pt-5 font-sans text-lg font-medium">
          <MapIcon className="h-7 w-7" aria-hidden="true" />
          <span>{t("tripNavbar.your_trips")}</span>
        </div>

        <NewTripButton
          onClick={closeMenu}
          className="!rounded-xl !border-primary !bg-white/45 !px-4 !py-3 hover:!bg-white/70 [&>span:first-child]:!bg-[#80691F] [&>span:last-child>span:first-child]:!font-sans [&>span:last-child>span:first-child]:!text-lg [&>span:last-child>span:first-child]:!not-italic [&>span:last-child>span:last-child]:!hidden"
        />
      </div>

      <section className="mt-4 flex min-h-0 flex-1 flex-col">
        <div className="mb-2 flex shrink-0 items-center justify-between px-1">
          <p className="font-sans text-[11px] font-bold uppercase tracking-[0.12em] text-[#80691F]">
            {t("tripNavbar.your_trips")}
          </p>

          {!isLoadingItineraries ? (
            <span className="rounded-full bg-white/55 px-2 py-0.5 font-sans text-[11px] font-semibold">
              {visibleItineraries.length + failedTrips.length}
            </span>
          ) : null}
        </div>

        <div
          id="journi-trip-list"
          className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 [scrollbar-color:rgba(84,70,29,0.5)_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#80691F]/50 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-1.5"
        >
          {!isLoadingItineraries && errorLoadingItineraries ? (
            <Alert status="error">{errorLoadingItineraries.message}</Alert>
          ) : null}

          {isLoadingItineraries ? (
            <>
              <Skeleton className="min-h-[72px] w-full rounded-xl" />
              <Skeleton className="min-h-[72px] w-full rounded-xl" />
              <Skeleton className="min-h-[72px] w-full rounded-xl" />
            </>
          ) : (
            <>
              {failedTrips.map(({ itinerary, trip, name }) => (
                <TripBlock
                  key={itinerary.id}
                  id={itinerary.trip_id}
                  name={name}
                  arrivalDate={trip.data.arrival_date}
                  departureDate={trip.data.departure_date}
                  duration={trip.data.duration}
                  badge={t("tripList.failed_badge")}
                  onClick={closeMenu}
                />
              ))}
              {visibleItineraries.map((itinerary) => (
                <TripBlock
                  key={itinerary.id}
                  id={itinerary.trip_id}
                  name={itinerary.data!.name!}
                  arrivalDate={itinerary.data!.arrival_date}
                  departureDate={itinerary.data!.departure_date}
                  duration={itinerary.data!.per_day_plan.length}
                  onClick={closeMenu}
                />
              ))}
            </>
          )}
        </div>
      </section>

      <div className="mt-3 shrink-0 border-t border-primary/40 pt-3">
        {subscription ? (
          <div className="mb-3">
            <TrialBlock
              subscriptionType={subscription.product.type}
              daysRemaining={subscriptionDaysLeft}
            />
          </div>
        ) : null}

        <div className="flex items-center gap-1">
          <Link
            to={Routes.PROFILE}
            onClick={closeMenu}
            className="group flex min-w-0 flex-1 items-center gap-3 px-2 py-2 transition-colors hover:bg-white/30"
          >
            <UserCircleIcon className="h-8 w-8 shrink-0" aria-hidden="true" />

            <span className="min-w-0 flex-1">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate font-feature text-lg font-semibold text-base-content">
                  {user?.preferred_name || "Your profile"}
                </span>
                <EllipsisHorizontalIcon
                  className="h-5 w-5 shrink-0 text-[#80691F]"
                  aria-hidden="true"
                />
              </span>
              {user?.email ? (
                <span className="block truncate font-sans text-xs text-text-secondary">
                  {user.email}
                </span>
              ) : null}
            </span>
          </Link>

          <Link
            to={Routes.SETTINGS}
            onClick={closeMenu}
            aria-label={t("button.settings")}
            title={t("button.settings")}
            className="group flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-white/40"
          >
            <Cog6ToothIcon
              className="h-6 w-6 transition-transform group-hover:rotate-45"
              aria-hidden="true"
            />
          </Link>
        </div>

        <button
          type="button"
          onClick={() => {
            closeMenu();
            void logout();
          }}
          className="mt-1 flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left font-sans text-sm font-medium text-error transition-colors hover:bg-error/10"
        >
          <ArrowRightStartOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
          {t("button.logout")}
        </button>
      </div>
    </nav>
  );
};

export default Navbar;